import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompileResult,
    SketchConstraintFormulaCompiler,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';
import type {
    SketchParametricPointHandle,
    SketchParametricSplineCurveDescriptor,
    SketchParametricSplinePointEvaluation,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SketchParametricSplineEvaluator} from '../core/SketchParametricSplineEvaluator';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';
import {readSketchConstraintBranchHintNumber} from '../utils/readSketchConstraintBranchHint';

/** 相切接触参数粗搜索分段数。 */
const TANGENT_SEED_SAMPLE_COUNT = 32;

/**
 * 精确可求值样条关系公式编译器。
 *
 * 当前承接 Bezier / 显式 B 样条与圆、圆弧或椭圆的相切关系。样条点和一阶导数
 * 均直接由当前控制点参数求值，禁止使用显示采样折线近似切向。
 */
export class SketchSplineConstraintFormulaCompiler implements SketchConstraintFormulaCompiler {
    /** 样条编译器接管的关系类型。 */
    public readonly relationKinds = ['Tangent'] as const;

    /** 样条参数曲线求值器。 */
    private readonly evaluator = new SketchParametricSplineEvaluator();

    /** 粗搜索复用输出点。 */
    private readonly seedPoint: SketchParametricSplinePointEvaluation = {x: 0, y: 0};

    /**
     * 编译样条关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        if (context.constraintState.relationKind !== 'Tangent') {
            return 'not-applicable';
        }

        const entityIds = context.resolveDirectEntityIds();

        if (entityIds.length !== 2) {
            return 'not-applicable';
        }

        const splineEntityId = entityIds.find((entityId) => context.splineCurveDescriptors.has(entityId));
        const implicitCurveEntityId = entityIds.find((entityId) => context.implicitCurveDescriptors.has(entityId));

        if (!splineEntityId || !implicitCurveEntityId) {
            return 'not-applicable';
        }

        const spline = context.splineCurveDescriptors.get(splineEntityId)!;
        const implicitCurve = context.implicitCurveDescriptors.get(implicitCurveEntityId)!;
        const persistedParameter = readSketchConstraintBranchHintNumber(
            context.constraintState,
            'spline-contact',
            'parameter',
        );
        /** 持久分支只提供 seed，并限制在当前样条定义域内，最终结果仍由精确方程决定。 */
        const seed = persistedParameter === null
            ? this.resolveNearestCenterParameter(context, spline, implicitCurve.center)
            : Math.min(spline.lastParameter, Math.max(spline.firstParameter, persistedParameter));
        const curveParameterId = context.system.addCurveParameter(
            `constraint:${context.constraintState.constraintId}:spline-contact`,
            seed,
        );

        context.system.addEquation(SketchParametricEquationFactory.splineImplicitCurveTangent(
            `constraint:${context.constraintState.constraintId}`,
            curveParameterId,
            spline,
            implicitCurve,
        ));
        context.registerResolvedBranchHint(() => ({
            kind: 'spline-contact',
            parameter: Math.min(
                spline.lastParameter,
                Math.max(spline.firstParameter, context.system.read(curveParameterId)),
            ),
        }));
        return 'compiled';
    }

    /**
     * 在样条定义域上粗搜索最靠近目标曲线中心的参数，作为接触分支 seed。
     *
     * 对象式实现同样以目标曲线中心附近的样条局部切向选择分支；迁移后只保留
     * 这一“选哪一支”的语义，最终接触点与切向由精确方程共同求解。
     *
     * @param context 编译上下文。
     * @param spline 样条描述符。
     * @param targetCenter 目标隐式曲线中心。
     * @returns 曲线参数 seed。
     */
    private resolveNearestCenterParameter(
        context: SketchConstraintFormulaCompileContext,
        spline: SketchParametricSplineCurveDescriptor,
        targetCenter: SketchParametricPointHandle,
    ): number {
        const centerX = context.system.read(targetCenter.xId);
        const centerY = context.system.read(targetCenter.yId);
        const span = spline.lastParameter - spline.firstParameter;
        const read = (parameterId: string): number => context.system.read(parameterId);
        let bestParameter = spline.firstParameter;
        let bestDistanceSquared = Number.POSITIVE_INFINITY;

        for (let index = 0; index <= TANGENT_SEED_SAMPLE_COUNT; index += 1) {
            const parameter = spline.firstParameter + span * index / TANGENT_SEED_SAMPLE_COUNT;

            this.evaluator.evaluatePoint(read, spline, parameter, this.seedPoint);
            const deltaX = this.seedPoint.x - centerX;
            const deltaY = this.seedPoint.y - centerY;
            const distanceSquared = deltaX * deltaX + deltaY * deltaY;

            if (distanceSquared < bestDistanceSquared) {
                bestDistanceSquared = distanceSquared;
                bestParameter = parameter;
            }
        }

        return bestParameter;
    }
}
